import React, { useState } from "react";
import { MessageCirclePlus, Send, ArrowRight } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import { useDispatch } from "react-redux";
import {
    clearInputValue,
    setInputDisabled,
    setIsNewChatClicked,
    setMicButtonDisabled,
    setSendButtonDisabled,
} from "../../../../store/features/ui/uiSlice";
import {
    deleteDataForTextLockFlow,
    deleteDataForVoiceLockFlow,
    setAccumulatedFinalTranscript,
    setCurrentInterimTranscript,
    setDisplayTranscript,
    setIsHideUserInputFields,
    setIsSuggestedQuestionsVisible,
    setRecordedBase64Data,
    setSelectedChatId,
    setSelectedSocketLanguageData,
    setTextLanguageDetectionData,
    setTranscriptType,
} from "../../../../store/features/message/textContentSlice";
import { API_ENDPOINTS } from "../../../../utils/constants";
import useLocalization from "../../../../hooks/useLocalization";
import { useAppContext } from "../../../../context/AppContext";
import { useLogger } from "../../../../utils/UnifiedLogger";
import { getClientIP } from "../../../../utils/util";
import Swal from "sweetalert2";
import { updateFlowStates } from "../../../../store/features/message/updateFlowStates";

function StateRelatedGrievance({ isNotRelated, message, userQuery, stateName,stateName_regional, stateCode }) {
    const { logInfo, logError } = useLogger("StateRelatedGrievance", true);
    const { t,getLanguage } = useLocalization();
    const { setInitialLanguage, sharedApiService, setIsNewChat, setSelectedLanguageData, userData, selectedLanguageData } = useAppContext();
    const dispatch = useDispatch();
    const registerStateGrievanceRequest = sharedApiService.sendRequest;

    const [isHovered, setIsHovered] = useState(false);
    const [isComplaintRegistering, setIsComplaintRegistering] = useState(false);

    // Extracted: Reset chat state
    const resetChatState = () => {
        dispatch(clearInputValue());
        dispatch(deleteDataForTextLockFlow());
        dispatch(deleteDataForVoiceLockFlow());
        dispatch(setAccumulatedFinalTranscript(""));
        dispatch(setCurrentInterimTranscript(""));
        dispatch(setDisplayTranscript(""));
        dispatch(setInputDisabled(false));
        dispatch(setIsHideUserInputFields(false));
        dispatch(setIsNewChatClicked(true));
        dispatch(setIsSuggestedQuestionsVisible(true));
        dispatch(setMicButtonDisabled(false));
        dispatch(setRecordedBase64Data(null));
        dispatch(setSelectedChatId(null));
        dispatch(setSelectedSocketLanguageData(null));
        dispatch(setSendButtonDisabled(false));
        dispatch(setTextLanguageDetectionData(null));
        dispatch(setTranscriptType(""));
        dispatch(updateFlowStates());
    };

    // Extracted: Reset to initial state
    const resetToInitialState = () => {
        resetChatState();
        setInitialLanguage();
        setIsNewChat(true);
        setSelectedLanguageData({ code: "en", label: "English" });
        sharedApiService.cancelAllRequests();
    };

    const handleNewChat = () => {
        setSelectedLanguageData({ code: "en", label: "English" });
        dispatch(setSelectedChatId(null));
        setIsNewChat(true);
        sharedApiService.cancelAllRequests();
        dispatch(clearInputValue());
        dispatch(setSelectedSocketLanguageData(null));
        dispatch(deleteDataForVoiceLockFlow());
        dispatch(deleteDataForTextLockFlow());
        dispatch(setIsHideUserInputFields(false));
        dispatch(setIsSuggestedQuestionsVisible(true));
        dispatch(setInputDisabled(false));
        dispatch(setSendButtonDisabled(false));
        dispatch(setMicButtonDisabled(false));
        dispatch(setIsNewChatClicked(true));
    };

    // Extracted: Show error alert
    const showErrorAlert = (customMessage = null) => {
        Swal.fire({
            icon: "error",
            title: t("grievance_submit.failTitle"),
            html: customMessage || `<p>${t("grievance_submit.failMessage")}</p>`,
            confirmButtonText: t("grievance_submit.ok"),
            confirmButtonColor: "#f44336",
            backdrop: `rgba(0,0,0,0.6)`,
            didOpen: () => {
                setIsComplaintRegistering(false);
            }
        });
    };

    // Extracted: Show success alert
    const showSuccessAlert = (res) => {
        Swal.fire({
            icon: "success",
            title: t("grievance_submit.successTitle"),
            html: `
                <div style="
                    background: linear-gradient(135deg, #f0fff4, #d1fae5);
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    margin-top: 10px;
                    animation: fadeIn 0.5s ease-in-out;
                ">
                <h3 style="
                    color: #16a34a;
                    font-size: 1.2rem;
                    font-weight: 700;
                    margin-bottom: 10px;
                ">
                    🎉 ${t("grievance_submit.successMessage")}
                </h3>
                ${res?.response_text
                    ? `
                        <div style="
                            margin-top: 15px;
                            padding: 12px 16px;
                            background: linear-gradient(90deg, #dcfce7, #bbf7d0);
                            border: 1px solid #22c55e;
                            border-radius: 12px;
                            display: inline-block;
                            text-align: left;
                        ">
                        <p style="color: #065f46; font-weight: 600; margin: 0;">
                            ${res.response_text}
                        </p>
                        </div>
                    `
                    : ""
                }
                </div>

                <style>
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                </style>
            `,
            confirmButtonText: t("grievance_submit.ok"),
            confirmButtonColor: "#4CAF50",
            backdrop: `rgba(0,0,0,0.6)`,
            didOpen: () => {
                setIsComplaintRegistering(false);
            }
        }).then((result) => {
            if (result.isConfirmed) {
                resetToInitialState();
            }
        });
    };

    // Extracted: Format error response text
    const formatErrorResponseText = (res) => {
        if (!res?.response_text) {
            return `<p>${t("grievance_submit.failMessage")}</p>`;
        }

        const isString = typeof res.response_text === "string";
        const message = isString ? res.response_text : res.response_text?.message;
        const error = isString ? "" : res.response_text?.error;

        return `<p>${message}</br>${error}</p>`;
    };

    const handleSubmit = async () => {
        if (isComplaintRegistering) return;

        setIsComplaintRegistering(true);
        logInfo('SUBMIT_STARTED', 'Starting form submission');
        try {
            const additionalData = {
                Document: "",
                IpAddress: await getClientIP(),
                Language: selectedLanguageData?.code || "en",
                OrgCode: stateCode,
                Remarks: userQuery || "",
                UserCode: userData?.id,
                MobileNumber: userData?.mobile || "9876543210",
            };

            logInfo('SUBMIT_STARTED', 'Starting form submission with additionalData', { additionalData });
            await handleSubmitGrievance(additionalData);

        } catch (error) {
            console.log(`Error is`, error);
            logInfo('SUBMIT_ERROR', 'Form submission failed', { error });
            setIsComplaintRegistering(false);
        }
    };

    const handleSubmitGrievance = async (additionalData) => {
        try {
            setIsComplaintRegistering(true);
            logInfo("SUBMIT_GRIEVANCE", "User submitted a grievance with additionalData", additionalData);

            const grivancePayload = { ...additionalData };
            logInfo(`SUBMIT_GRIEVANCE_PAYLOAD`, 'Grievance submission payload', grivancePayload);

            registerStateGrievanceRequest({
                method: "POST",
                url: API_ENDPOINTS.registerStateGrievance,
                headers: { "Content-Type": "application/json" },
                body: grivancePayload,
                abortable: true,
                isEncrypted: true,
                onStart: () => {
                    logInfo(`SUBMIT_GRIEVANCE_INITIATED`, 'Grievance submission initiated', grivancePayload);
                    setIsComplaintRegistering(true);
                },
                onProgress: (message) => {
                    logInfo(`SUBMIT_GRIEVANCE_PROGRESS`, 'Grievance submission in progress', message);
                },
                onStream: () => {
                    logInfo(`SUBMIT_GRIEVANCE_STREAM`, 'Grievance submission stream', grivancePayload);
                },
                onSuccess: (res) => {
                    logInfo(`SUBMIT_GRIEVANCE_SUCCESS`, 'Grievance submission successful', res);

                    if (res?.status_code !== 200) {
                        showErrorAlert(formatErrorResponseText(res));
                    } else {
                        showSuccessAlert(res);
                    }
                },
                onError: (err) => {
                    setIsComplaintRegistering(false);
                    logError(`SUBMIT_GRIEVANCE_ERROR`, 'Grievance submission failed', err);
                    if (err.code === 'ERR_NETWORK' || err.message.includes('Network ')) {
                        showErrorAlert();
                    }
                },
                onCancel: (cancelInfo) => {
                    setIsComplaintRegistering(false);
                    logInfo(`SUBMIT_GRIEVANCE_CANCELLED`, 'Grievance submission cancelled', cancelInfo);
                },
                onComplete: () => {
                    logInfo(`SUBMIT_GRIEVANCE_COMPLETED`, 'Grievance submission completed');
                },
            });
        } catch (error) {
            showErrorAlert(`
                <p>${t("grievance_submit.failMessage")}</p>
                <p>${t("grievance_submit.failSuggestion")}</p>
            `);
        }
    };

    const formatText = (txt = "") =>
        txt
            .trim()
            .replace(/(?<=[^\s*])(\*{1,2})(?=[A-Za-z0-9])/g, '$1 ')
            .replace(/(\d+\.\s)/g, '\n$1');

    if (!isNotRelated) return null;

    return (
        <div className="w-full mt-6 px-4 animate-fadeIn">
            <div
                className="relative max-w-3xl mx-auto"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* SUBMIT LOADER OVERLAY */}
                {isComplaintRegistering && (
                    <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white/80 dark:bg-black/70 rounded-2xl">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl px-6 py-5 flex flex-col items-center gap-3">
                            <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                {t('registerningGrievance')}
                            </p>
                        </div>
                    </div>
                )}
                <div>
                    {/* Message content */}
                    <div className="space-y-4 mb-6">
                        <div className="p-5 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 rounded-2xl border border-purple-100 shadow-inner">
                            <div className="select-text selection:bg-blue-500 selection:text-white prose prose-sm max-w-none text-gray-700">
                                <ReactMarkdown>{formatText(message)}</ReactMarkdown>
                            </div>
                        </div>

                        {/* User query display */}
                        {userQuery && (
                            <div className="relative p-4 bg-white rounded-2xl border-2 border-dashed border-purple-200 shadow-sm">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-2">
                                    <div className="px-3 py-1 bg-white border border-purple-200 rounded-full">
                                        <span className="text-xs font-semibold text-purple-600">
                                            {t('yourQuery')}
                                        </span>
                                    </div>

                                    {stateName && (
                                        <div className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-full">
                                            <span className="text-xs font-semibold text-purple-700">
                                                {getLanguage() !== 'en' ? stateName_regional : stateName}
                                            </span>
                                            {stateCode && (
                                                <span className="text-xs text-purple-500">
                                                    ({stateCode})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Query text */}
                                <p className="select-text selection:bg-blue-500 selection:text-white text-sm text-gray-600 italic">
                                    "{userQuery}"
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleSubmit}
                            disabled={isComplaintRegistering}
                            className={`
                                flex-1 group relative px-6 py-4 rounded-2xl font-semibold
                                transition-all duration-300 transform
                                ${isComplaintRegistering
                                    ? 'bg-gradient-to-r from-purple-400 to-pink-400 text-white cursor-wait'
                                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-xl hover:scale-105 active:scale-95'
                                }
                                disabled:opacity-50
                            `}
                        >
                            <span className="flex items-center justify-center gap-2">
                                {isComplaintRegistering ? (
                                    <>
                                        <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>{t('submitting')}</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                        <span>{t('submitGrievance')}</span>
                                        <ArrowRight className="w-4 h-4 opacity-0 -ml-4 transition-all group-hover:opacity-100 group-hover:ml-0" />
                                    </>
                                )}
                            </span>

                            {/* Shimmer effect */}
                            {!isComplaintRegistering && (
                                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                                    <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                                </div>
                            )}
                        </button>

                        <button
                            onClick={handleNewChat}
                            className="px-6 py-4 rounded-2xl font-semibold border-2 border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all duration-300 hover:shadow-md transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                        >
                            <MessageCirclePlus className="w-5 h-5" />
                            <span>{t('newChat' || 'Start New Chat')}</span>
                        </button>
                    </div>
                </div>

                {/* Floating particles effect */}
                {isHovered && (
                    <>
                        <div className="absolute top-10 right-10 w-2 h-2 bg-purple-400 rounded-full animate-float-slow"></div>
                        <div className="absolute bottom-10 left-10 w-2 h-2 bg-pink-400 rounded-full animate-float-slow animation-delay-200"></div>
                        <div className="absolute top-20 left-20 w-1.5 h-1.5 bg-blue-400 rounded-full animate-float-slow animation-delay-400"></div>
                    </>
                )}
            </div>

            <style jsx>{`
                @keyframes gradient-x {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                
                @keyframes float-slow {
                    0%, 100% { transform: translate(0, 0); opacity: 0; }
                    50% { transform: translate(10px, -20px); opacity: 1; }
                }
                
                .animate-gradient-x {
                    background-size: 200% 200%;
                    animation: gradient-x 3s ease infinite;
                }
                
                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }
                
                .animate-float-slow {
                    animation: float-slow 3s ease-in-out infinite;
                }
                
                .animation-delay-200 {
                    animation-delay: 0.2s;
                }
                
                .animation-delay-400 {
                    animation-delay: 0.4s;
                }
            `}</style>
        </div>
    );
}

export default StateRelatedGrievance;